package com.islamicmedia.shared.domain.model

/** How sure the scan is that a spine is a real, correctly named book. Drives the tag and the default tick. */
enum class BulkItemStatus {
    /** Found in a book catalog, title and author agree. Ticked by default. */
    MATCHED,
    /** Plausible catalog match, worth a look. */
    CHECK,
    /** Confidently read but the catalogs don't know it. */
    UNVERIFIED,
    /** A weak read with no catalog hit. */
    GUESS,
    /** A spine was there but nothing could be read. The user can type its title. */
    UNREADABLE,
    /** Typed in by the user. */
    MANUAL
}

/** One row on the review screen. */
data class BulkScanItem(
    val id: String,
    val title: String,
    val author: String = "",
    val year: String? = null,
    val coverUrl: String? = null,
    val status: BulkItemStatus,
    /** How many spines in the photo are this book (more than 1 = copies or volumes were merged). */
    val copies: Int = 1,
    /** Small upright picture of an unreadable spine, base64 JPEG, so the user can read it themselves. */
    val thumbBase64: String? = null,
    val selected: Boolean = false,
    /** Where the spine sits in the photo (left to right / top to bottom); rows are kept in this order. */
    val position: Int = 0
) {
    /** A row can only be analysed once it has a title. */
    val canAnalyze: Boolean get() = title.isNotBlank()
}

enum class BulkScanPhase {
    /** Waiting for the user to take or choose a photo. */
    PICK,
    /** The photo is being read. */
    SCANNING,
    /** The user is checking the list. */
    REVIEW,
    /** The scan failed; [BulkScanState.error] says why. */
    ERROR,
    /** The ticked books are being analysed on the server (or have finished); see [BulkScanState.results]. */
    RESULTS
}

/** Where one book is in a running batch. */
enum class BulkResultStatus { QUEUED, RUNNING, DONE, FAILED, REFUSED }

/** The same three tiers the rest of the app shows (Minor / Moderate / Major). Never a number. */
enum class BulkVerdict { MINOR, MODERATE, MAJOR }

/** One book's line on the results screen. */
data class BulkResultItem(
    val id: String,
    val title: String,
    val author: String = "",
    val year: String? = null,
    val coverUrl: String? = null,
    val status: BulkResultStatus,
    val verdict: BulkVerdict? = null,
    /** Why a book was FAILED/REFUSED: "out_of_searches", "usage_cap", "analysis_failed", ... */
    val error: String? = null,
    /** Where the book sat in the list the user ticked (stable). */
    val position: Int = 0,
    /** ISO time the book last changed state; for a DONE book that is when it finished. */
    val updatedAt: String? = null
)

/**
 * A running (or just finished) server-side batch. Lives OUTSIDE [BulkScanState] on purpose: the user
 * can close the scan screens, open a book, or go back to searching, and the batch must stay reachable
 * from the main screen until they dismiss it.
 */
data class BulkBatchState(
    val batchId: String,
    val results: List<BulkResultItem> = emptyList(),
    val done: Boolean = false,
    /** The last progress check couldn't reach the server (the batch keeps running there). */
    val offline: Boolean = false,
    val error: String? = null
) {
    val total: Int get() = results.size
    val finished: Int get() = results.count { it.status != BulkResultStatus.QUEUED && it.status != BulkResultStatus.RUNNING }
    val analyzed: Int get() = results.count { it.status == BulkResultStatus.DONE }
}

data class BulkScanState(
    val phase: BulkScanPhase = BulkScanPhase.PICK,
    val items: List<BulkScanItem> = emptyList(),
    val error: String? = null,
    /** True while the batch is being started (the button was tapped, the server hasn't answered yet). */
    val starting: Boolean = false,
    /** The server-side batch being shown in [BulkScanPhase.RESULTS]. */
    val batchId: String? = null,
    val results: List<BulkResultItem> = emptyList(),
    /** True once every book in the batch has finished (analysed, failed or refused). */
    val batchDone: Boolean = false,
    /** True when the last progress check couldn't reach the server (the batch keeps running there). */
    val offline: Boolean = false,
    /** True while the photo is still being read: rows keep arriving, so the list is not final yet. */
    val scanning: Boolean = false,
    /** How many spines the first pass located in the photo (0 until known). Shown as "N of ~M". */
    val foundCount: Int = 0
) {
    val resultsDone: Int get() = results.count { it.status == BulkResultStatus.DONE }
    val resultsFinished: Int get() = results.count { it.status != BulkResultStatus.QUEUED && it.status != BulkResultStatus.RUNNING }

    /** Ticked rows that have a title: what "Analyze N books" would run. */
    val selectedCount: Int get() = items.count { it.selected && it.canAnalyze }
}
